import { useCallback, useEffect } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { ApiQueryParams } from '@/api/Api';
import {
  listProjects,
  type GetProjectListRequest,
  type GetProjectListResponse,
} from '@/api/project/list';
import { readProject, type ReadProjectResponse } from '@/api/project/read';
import {
  createProject,
  type CreateProjectResponse,
  type CreateProjectRequest,
} from '@/api/project/create';
import {
  updateProject,
  type UpdateProjectResponse,
} from '@/api/project/update';
import {
  deleteProject,
  type DeleteProjectResponse,
} from '@/api/project/delete';
import { cloneProject, type CloneProjectResponse } from '@/api/project/clone';
import {
  importProjects,
  type ImportProjectRequest,
  type ImportProjectResponse,
} from '@/api/project/import';
import {
  exportProjects,
  type ExportProjectRequest,
  type ExportProjectResponse,
} from '@/api/project/export';
import type { Project } from '@/interfaces/project.interface';
import { buildParams, mergeApiQueryParams } from './utils';
import { notifyApiError, shouldSuppressErrorToast } from './error';
import {
  saveProjectToStorage,
  queueProjectUpdate,
  getProjectFromStorage,
} from '@/utils/projectStorage';

const PROJECT_LIST_KEY = 'projects';
const PROJECT_ITEM_KEY = 'project';

type ProjectsQueryOptions = Omit<
  UseQueryOptions<
    GetProjectListResponse,
    unknown,
    GetProjectListResponse,
    [typeof PROJECT_LIST_KEY, GetProjectListRequest]
  >,
  'queryKey' | 'queryFn'
>;

export const useProjects = (
  params?: Partial<GetProjectListRequest>,
  options?: ProjectsQueryOptions
) => {
  const finalParams: GetProjectListRequest = {
    page: params?.page ?? 1,
    page_limit: params?.page_limit ?? 50,
    ...mergeApiQueryParams<GetProjectListRequest>(
      'project',
      params ?? {},
      'list'
    ),
    ...(params ?? {}),
  };

  const query = useQuery<GetProjectListResponse, unknown>({
    queryKey: [PROJECT_LIST_KEY, finalParams],
    queryFn: async () => {
      const response = await listProjects(finalParams);
      return response.data;
    },
    ...options,
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'project', action: 'list' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

export const usePrefetchProjects = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (params?: Partial<GetProjectListRequest>) => {
      const finalParams: GetProjectListRequest = {
        page: params?.page ?? 1,
        page_limit: params?.page_limit ?? 50,
        ...mergeApiQueryParams<GetProjectListRequest>(
          'project',
          params ?? {},
          'list'
        ),
        ...(params ?? {}),
      };

      try {
        await queryClient.prefetchQuery({
          queryKey: [PROJECT_LIST_KEY, finalParams],
          queryFn: async () => {
            const response = await listProjects(finalParams);
            return response.data;
          },
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[query] Prefetch projects failed', error);
        }
      }
    },
    [queryClient]
  );
};

type ProjectQueryOptions = Omit<
  UseQueryOptions<
    ReadProjectResponse,
    unknown,
    ReadProjectResponse,
    [typeof PROJECT_ITEM_KEY, string | undefined, ApiQueryParams]
  >,
  'queryKey' | 'queryFn'
>;

export const useProject = (
  projectId?: string,
  params?: Partial<ApiQueryParams>,
  options?: ProjectQueryOptions
) => {
  const finalParams = buildParams('project', params, 'read');

  const query = useQuery<ReadProjectResponse, unknown>({
    queryKey: [PROJECT_ITEM_KEY, projectId, finalParams],
    queryFn: async () => {
      if (!projectId) {
        throw new Error('projectId is required to fetch project details');
      }
      const response = await readProject(projectId, finalParams);
      return response.data;
    },
    ...options,
    enabled: Boolean(projectId) && (options?.enabled ?? true),
  });

  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  useEffect(() => {
    if (!suppressErrorToast && query.isError && query.error) {
      notifyApiError(query.error, { entity: 'project', action: 'read' });
    }
  }, [suppressErrorToast, query.isError, query.error, query.errorUpdatedAt]);

  return query;
};

export const usePrefetchProject = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (projectId?: string, params?: Partial<ApiQueryParams>) => {
      if (!projectId) return;

      const finalParams = buildParams('project', params, 'read');

      try {
        await queryClient.prefetchQuery({
          queryKey: [PROJECT_ITEM_KEY, projectId, finalParams],
          queryFn: async () => {
            const response = await readProject(projectId, finalParams);
            return response.data;
          },
        });
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[query] Prefetch project failed', error);
        }
      }
    },
    [queryClient]
  );
};

type CreateProjectVariables = {
  data: CreateProjectRequest;
  params?: Partial<ApiQueryParams>;
};

type CreateProjectOptions = UseMutationOptions<
  CreateProjectResponse,
  unknown,
  CreateProjectVariables
>;

export const useCreateProject = (options?: CreateProjectOptions) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CreateProjectResponse, unknown, CreateProjectVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = buildParams('project', params, 'create');
      const response = await createProject(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'project', action: 'create' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [PROJECT_LIST_KEY] });
      const createdId = variables.data?.id ?? data?.data?.id;
      if (createdId) {
        queryClient.invalidateQueries({
          queryKey: [PROJECT_ITEM_KEY, createdId],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type UpdateProjectVariables = {
  id: string;
  data: Partial<Project>;
  params?: Partial<ApiQueryParams>;
};

type UpdateProjectOptions = UseMutationOptions<
  UpdateProjectResponse,
  unknown,
  UpdateProjectVariables
>;

export const useUpdateProject = (options?: UpdateProjectOptions) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<UpdateProjectResponse, unknown, UpdateProjectVariables>({
    mutationFn: async ({ id, data, params }) => {
      // Check if offline
      const isOffline = !navigator.onLine;

      if (isOffline) {
        // Queue the update for later
        queueProjectUpdate(id, data);
        // Throw error to trigger onError, but we'll handle it gracefully
        throw new Error('OFFLINE_QUEUED');
      }

      const finalParams = buildParams('project', params, 'update');
      const response = await updateProject(id, finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      // If offline, the update was queued, so don't show error toast
      const isOfflineError =
        error instanceof Error && error.message === 'OFFLINE_QUEUED';

      if (!isOfflineError && !suppressErrorToast) {
        notifyApiError(error, { entity: 'project', action: 'update' });
      }

      // Even if offline, optimistically update local storage
      if (isOfflineError && variables.id) {
        const currentProject = getProjectFromStorage(variables.id);
        if (currentProject) {
          const updatedProject = { ...currentProject, ...variables.data };
          saveProjectToStorage(updatedProject);
        }
      }

      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      // Update local storage with the updated project data
      if (variables.id) {
        const currentProject = getProjectFromStorage(variables.id);
        if (currentProject) {
          // Merge the updated data with existing project
          // Note: API response data.data only contains {id: string}, so we use variables.data
          const updatedProject = { ...currentProject, ...variables.data };
          saveProjectToStorage(updatedProject);
        } else {
          // If project not in cache, create a minimal project object
          // This shouldn't happen often, but handle it gracefully
          const updatedProject = {
            id: variables.id,
            title: variables.data.title || variables.id,
            ...variables.data,
          } as Project;
          saveProjectToStorage(updatedProject);
        }
      }

      // Invalidate and refetch queries
      queryClient.invalidateQueries({ queryKey: [PROJECT_LIST_KEY] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [PROJECT_ITEM_KEY, variables.id],
        });
      }

      onSuccess?.(data, variables, context);
    },
  });
};

type DeleteProjectVariables = {
  id: string;
  params?: Partial<ApiQueryParams>;
};

type DeleteProjectOptions = UseMutationOptions<
  DeleteProjectResponse,
  unknown,
  DeleteProjectVariables
>;

export const useDeleteProject = (options?: DeleteProjectOptions) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<DeleteProjectResponse, unknown, DeleteProjectVariables>({
    mutationFn: async ({ id, params }) => {
      const finalParams = buildParams('project', params, 'delete');
      const response = await deleteProject(id, finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'project', action: 'delete' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [PROJECT_LIST_KEY] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [PROJECT_ITEM_KEY, variables.id],
        });
      }
      onSuccess?.(data, variables, context);
    },
  });
};

type CloneProjectVariables = {
  id: string;
  params?: Partial<ApiQueryParams>;
};

type CloneProjectOptions = UseMutationOptions<
  CloneProjectResponse,
  unknown,
  CloneProjectVariables
>;

export const useCloneProject = (options?: CloneProjectOptions) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<CloneProjectResponse, unknown, CloneProjectVariables>({
    mutationFn: async ({ id, params }) => {
      const finalParams = buildParams('project', params, 'clone');
      const response = await cloneProject(id, finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'project', action: 'clone' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [PROJECT_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ImportProjectVariables = {
  data: unknown;
  params?: Partial<ImportProjectRequest>;
};

type ImportProjectOptions = UseMutationOptions<
  ImportProjectResponse,
  unknown,
  ImportProjectVariables
>;

export const useImportProject = (options?: ImportProjectOptions) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ImportProjectResponse, unknown, ImportProjectVariables>({
    mutationFn: async ({ data, params }) => {
      const finalParams = mergeApiQueryParams<ImportProjectRequest>(
        'project',
        params ?? {},
        'import' // Use 'list' service type since import filters like list endpoint
      );
      const response = await importProjects(finalParams, data);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'project', action: 'import' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [PROJECT_LIST_KEY] });
      onSuccess?.(data, variables, context);
    },
  });
};

type ExportProjectVariables = {
  params?: Partial<ExportProjectRequest>;
};

type ExportProjectOptions = UseMutationOptions<
  ExportProjectResponse,
  unknown,
  ExportProjectVariables
>;

export const useExportProject = (options?: ExportProjectOptions) => {
  const queryClient = useQueryClient();

  const { onError, onSuccess, ...restOptions } = options ?? {};
  const suppressErrorToast = shouldSuppressErrorToast(options?.meta);

  return useMutation<ExportProjectResponse, unknown, ExportProjectVariables>({
    mutationFn: async ({ params }) => {
      // Extract filters to preserve them (buildParams doesn't include filters)
      const { filters, ...paramsWithoutFilters } = params ?? {};
      const baseParams = buildParams('project', paramsWithoutFilters, 'export');
      // Add filters back to the params
      const finalParams = {
        ...baseParams,
        ...(filters ? { filters } : {}),
      };
      const response = await exportProjects(finalParams);
      return response.data;
    },
    ...restOptions,
    onError: (error, variables, context) => {
      if (!suppressErrorToast) {
        notifyApiError(error, { entity: 'project', action: 'export' });
      }
      onError?.(error, variables, context);
    },
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  });
};
